import React, { createContext, useContext, useState } from 'react'

export interface ModalContextProps {
	isOpen: boolean
	step: number
	openModal: () => void
	closeModal: () => void
	nextStep: () => void
	prevStep: () => void
	jumpTo: (step: number) => void
}

const ModalContext = createContext<ModalContextProps | undefined>(undefined)

export const useModalContext = (): ModalContextProps | undefined => {
	const context = useContext(ModalContext)
	if (context === null) {
		throw new Error('useModalContext must be used within a ModalProvider')
	}
	return context
}

export interface ModalProviderProps {
	children: React.ReactNode
	modalDefaultState?: boolean
	initStep?: number
}

export const ModalProvider: React.FC<ModalProviderProps> = ({
	children,
	modalDefaultState = false,
	initStep = 0,
}: ModalProviderProps) => {
	const [step, setStep] = useState<number>(initStep)
	const [isOpen, setIsOpen] = useState<boolean>(modalDefaultState)

	const openModal = (): void => {
		setIsOpen(true)
	}
	const jumpTo = (step: number): void => {
		setStep(step)
	}
	const closeModal = (): void => {
		setIsOpen(false)
		setStep(0)
	}
	const nextStep = (): void => {
		setStep((prevStep) => prevStep + 1)
	}
	const prevStep = (): void => {
		setStep((prevStep) => prevStep - 1)
	}

	return (
		<ModalContext.Provider
			value={{
				isOpen,
				step,
				openModal,
				jumpTo,
				closeModal,
				nextStep,
				prevStep,
			}}
		>
			{children}
		</ModalContext.Provider>
	)
}
